import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Avatar, Tooltip } from '@mui/material';
import { Edit, Add, Delete, CheckCircle, Comment, Schedule, PersonAddAlt } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { requestGetActivities } from '../redux/sagas/requests/activity';
import { formatTimeAgo, formatDateTime } from '../utils/dateUtils';
import { resolveActivityLink } from '../utils/activityNavigation';

const toActivityRow = (activity) => ({
    id: activity._id,
    displayCategory: activity.displayCategory || 'default',
    text: activity.description,
    time: activity.createdAt,
    user: activity.user?.fullName || '',
    linkTo: resolveActivityLink(activity),
});

const ActivityList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const ActivityItem = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'clickable',
})(({ theme, clickable }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: clickable ? 'pointer' : 'default',
    '&:last-child': {
        borderBottom: 'none',
    },
    ...(clickable && {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        borderRadius: theme.shape.borderRadius,
        marginLeft: theme.spacing(-1),
        marginRight: theme.spacing(-1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    }),
}));

const ActivityIcon = styled(Avatar)(({ theme, activitytype }) => ({
    width: 32,
    height: 32,
    marginRight: theme.spacing(1.5),
    backgroundColor:
        activitytype === 'create' ? theme.palette.success.main :
        activitytype === 'update' ? theme.palette.info.main :
        activitytype === 'delete' ? theme.palette.error.main :
        activitytype === 'complete' ? theme.palette.success.main :
        activitytype === 'comment' ? theme.palette.primary.main :
        activitytype === 'time' ? theme.palette.secondary.main :
        activitytype === 'assign' ? theme.palette.warning.main :
        theme.palette.grey[500],
}));

const ActivityContent = styled(Box)({
    flex: 1,
    minWidth: 0,
});

const ActivityText = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
}));

const ActivityTime = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    marginTop: 2,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const getActivityIcon = (category) => {
    switch (category) {
        case 'create':
        case 'add':
            return <Add fontSize="small" />;
        case 'update':
        case 'edit':
            return <Edit fontSize="small" />;
        case 'delete':
        case 'remove':
            return <Delete fontSize="small" />;
        case 'complete':
            return <CheckCircle fontSize="small" />;
        case 'comment':
            return <Comment fontSize="small" />;
        case 'time':
            return <Schedule fontSize="small" />;
        case 'assign':
            return <PersonAddAlt fontSize="small" />;
        default:
            return <Edit fontSize="small" />;
    }
};

const TeamActivityWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const { data: activities, isLoading, error, mutate } = useFetchQueue(widgetId, {
        apiCall: () => requestGetActivities(),
        cacheKey: 'activities-all',
        transform: (response) => {
            const activities = response?.activities || response || [];
            return activities
                .map(toActivityRow)
                .sort((a, b) => new Date(b.time) - new Date(a.time))
                .slice(0, 25);
        },
    });

    useSocketEvent('activity:new', (activity) => mutate((prev) => {
        const row = toActivityRow(activity);
        const list = (prev || []).filter((a) => a.id !== row.id);
        return [row, ...list].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 25);
    }));

    const hasData = (activities || []).length > 0;

    const handleRowActivate = (linkTo) => {
        if (linkTo) {
            navigate(linkTo);
        }
    };

    return (
        <BaseWidget
            title="Team Activity"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <Typography variant="body2">
                        No recent activity
                    </Typography>
                </EmptyState>
            ) : (
                <ActivityList>
                    {(activities || []).map((activity, index) => (
                        <ActivityItem
                            key={activity.id || index}
                            clickable={Boolean(activity.linkTo)}
                            onClick={() => handleRowActivate(activity.linkTo)}
                            onKeyDown={(e) => {
                                if (activity.linkTo && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    handleRowActivate(activity.linkTo);
                                }
                            }}
                            role={activity.linkTo ? 'link' : undefined}
                            tabIndex={activity.linkTo ? 0 : undefined}
                        >
                            <ActivityIcon activitytype={activity.displayCategory}>
                                {getActivityIcon(activity.displayCategory)}
                            </ActivityIcon>
                            <ActivityContent>
                                <ActivityText>
                                    {activity.user} - {activity.text}
                                </ActivityText>
                                <Tooltip title={formatDateTime(activity.time)}>
                                    <ActivityTime>
                                        {formatTimeAgo(activity.time)}
                                    </ActivityTime>
                                </Tooltip>    
                            </ActivityContent>
                        </ActivityItem>
                    ))}
                </ActivityList>
            )}
        </BaseWidget>
    );
};

export default TeamActivityWidget;
